import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Slider,
  Grid,
  Divider,
  Tooltip,
  IconButton
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';

function ROICalculator() {
  // Removed "providers" state to focus on single practitioner
  const [sessionsPerWeek, setSessionsPerWeek] = useState(20);
  const [adminHours, setAdminHours] = useState(10); // Hours spent on notes/billing/intake
  const [avgSessionRate, setAvgSessionRate] = useState(150); // New: Drives all value calcs

  const calculations = useMemo(() => {
    // 1. Time Savings Logic
    // Assumption: Halespring's AI/Auto-billing saves ~60% of admin time
    const weeklyHoursSaved = adminHours * 0.6;
    const monthlyHoursSaved = weeklyHoursSaved * 4;

    // Value of Time: Calculated as "Opportunity Cost" (Potential Billable Hours reclaimed)
    // We assume you could fill 80% of that saved time with new clients
    const valueOfTimeSaved = (monthlyHoursSaved * 0.8) * avgSessionRate;

    // 2. Referral/Growth Logic
    // Assumption: Active Referral Network adds ~15% to caseload volume
    const newSessionsPerWeek = Math.round(sessionsPerWeek * 0.15);
    const monthlyReferralRevenue = newSessionsPerWeek * avgSessionRate * 4;

    // 3. Totals
    const totalMonthlyGain = valueOfTimeSaved + monthlyReferralRevenue;
    const platformCost = 99; // Standard Tier
    const netROI = totalMonthlyGain - platformCost;

    // ROI % = (Net Profit / Cost) * 100
    const roiPercentage = Math.round((netROI / platformCost) * 100);

    return {
      monthlyHoursSaved: Math.round(monthlyHoursSaved),
      valueOfTimeSaved: Math.round(valueOfTimeSaved),
      monthlyReferralRevenue: Math.round(monthlyReferralRevenue),
      totalMonthlyGain: Math.round(totalMonthlyGain),
      roiPercentage: roiPercentage > 0 ? roiPercentage : 0
    };
  }, [sessionsPerWeek, adminHours, avgSessionRate]);

  return (
      <Card className="max-w-5xl mx-auto shadow-xl rounded-3xl overflow-visible">
        <CardContent className="p-8 lg:p-12">
          <Typography variant="h4" className="mb-2 text-center font-bold text-gray-900">
            ROI Calculator
          </Typography>
          <Typography variant="body1" className="mb-12 text-center text-gray-500">
            Estimate the value of automating your practice.
          </Typography>

          <Grid container spacing={6}>
            {/* LEFT COLUMN: INPUTS */}
            <Grid item xs={12} md={7}>
              <Box className="space-y-10 pr-0 md:pr-8">

                {/* Input 1: Caseload */}
                <Box>
                  <div className="flex justify-between items-end mb-4">
                    <Typography variant="subtitle1" className="font-bold text-gray-700">
                      Current Sessions per Week
                    </Typography>
                    <Typography variant="h6" className="text-blue-600 font-bold">
                      {sessionsPerWeek}
                    </Typography>
                  </div>
                  <Slider
                      value={sessionsPerWeek}
                      onChange={(e, value) => setSessionsPerWeek(value)}
                      min={5}
                      max={60}
                      valueLabelDisplay="auto"
                      sx={{ color: '#3b82f6', height: 8 }}
                  />
                  <Typography variant="caption" className="text-gray-400">
                    Used to estimate referral network growth potential
                  </Typography>
                </Box>

                {/* Input 2: Admin Time */}
                <Box>
                  <div className="flex justify-between items-end mb-4">
                    <div className="flex items-center gap-2">
                      <Typography variant="subtitle1" className="font-bold text-gray-700">
                        Hours Spent on Admin (Weekly)
                      </Typography>
                      <Tooltip title="Includes notes, billing, scheduling, and phone tag." arrow>
                        <IconButton size="small"><InfoOutlined fontSize="small" /></IconButton>
                      </Tooltip>
                    </div>
                    <Typography variant="h6" className="text-blue-600 font-bold">
                      {adminHours} hrs
                    </Typography>
                  </div>
                  <Slider
                      value={adminHours}
                      onChange={(e, value) => setAdminHours(value)}
                      min={1}
                      max={30}
                      valueLabelDisplay="auto"
                      sx={{ color: '#3b82f6', height: 8 }}
                  />
                </Box>

                {/* Input 3: Session Rate */}
                <Box>
                  <div className="flex justify-between items-end mb-4">
                    <Typography variant="subtitle1" className="font-bold text-gray-700">
                      Average Session Rate ($)
                    </Typography>
                    <Typography variant="h6" className="text-blue-600 font-bold">
                      ${avgSessionRate}
                    </Typography>
                  </div>
                  <Slider
                      value={avgSessionRate}
                      onChange={(e, value) => setAvgSessionRate(value)}
                      min={50}
                      max={500}
                      step={10}
                      valueLabelDisplay="auto"
                      sx={{ color: '#3b82f6', height: 8 }}
                  />
                  <Typography variant="caption" className="text-gray-400">
                    Used to calculate the value of time reclaimed
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* RIGHT COLUMN: RESULTS */}
            <Grid item xs={12} md={5}>
              <Box className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-2xl h-full border border-blue-100 flex flex-col justify-between">

                <div>
                  <Typography variant="h6" className="mb-6 font-bold text-gray-800">
                    Potential Monthly Impact
                  </Typography>

                  <div className="space-y-4">
                    {/* Metric A */}
                    <div className="flex justify-between items-center">
                      <Typography className="text-gray-600">Billable Hours Gained:</Typography>
                      <Typography className="font-bold text-lg">
                        {calculations.monthlyHoursSaved} hrs/mo
                      </Typography>
                    </div>

                    {/* Metric B */}
                    <div className="flex justify-between items-center">
                      <Typography className="text-gray-600">Value of Time Reclaimed:</Typography>
                      <Typography className="font-bold text-lg text-green-600">
                        +${calculations.valueOfTimeSaved.toLocaleString()}
                      </Typography>
                    </div>

                    {/* Metric C */}
                    <div className="flex justify-between items-center">
                      <Typography className="text-gray-600">New Referral Revenue:</Typography>
                      <Typography className="font-bold text-lg text-blue-600">
                        +${calculations.monthlyReferralRevenue.toLocaleString()}
                      </Typography>
                    </div>
                  </div>

                  <Divider className="my-6" />

                  {/* BIG NUMBER */}
                  <div className="text-center">
                    <Typography variant="body2" className="text-gray-500 uppercase tracking-wide font-semibold mb-2">
                      Potential Monthly Value
                    </Typography>
                    <Typography variant="h3" align="center" className="font-extrabold text-gray-900 mb-3">
                      ${calculations.totalMonthlyGain.toLocaleString()}
                    </Typography>

                  </div>
                </div>

              </Box>
            </Grid>
          </Grid>

          {/* DISCLAIMER FOOTER */}
          <Box className="mt-8 pt-4 border-t border-gray-100 text-center">
            <Typography variant="caption" className="text-gray-400 block italic leading-relaxed">
              * Disclaimer: These figures are notional estimates for illustrative purposes only and do not guarantee actual results.
              "Value of Time Reclaimed" assumes 80% of saved admin hours are converted into billable sessions.
              "Referral Revenue" estimates a 15% increase in caseload volume.
            </Typography>
          </Box>
        </CardContent>
      </Card>
  );
}

export default ROICalculator;